// The fields of a VFR ICAO flight plan (ICAO Doc 4444 appendix 2), filled
// in from the plan, the aircraft and the pilot, ready to copy into the
// filing form. Ported from webplan-modern's FplPanel, with its formatting
// brought into line with the form:
//   * item 7 is the registration without its hyphen (OHCAM, not OH-CAM);
//   * item 15's speed is N plus FOUR digits of knots (N0115, not N115);
//   * an aerodrome without a four-letter ICAO code is ZZZZ, its name going
//     to item 18 as DEP/ or DEST/;
//   * item 19's endurance is the usable fuel's (fuelPlan's
//     enduranceMinutes), not webplan's trip time plus reserve.
// Free text is upper-cased and transliterated to the form's character set
// (Ä and Å to A, Ö to O). Pure; see navigation.ts.

import { pbnItem18 } from './aircraftOperating'

export interface IcaoFlightPlanInput {
  registration: string
  /** Item 9's type designator, e.g. C172, as the pilot knows it. */
  aircraftType: string | null
  /** Item 10a, e.g. SDGY. */
  equipment: string | null
  /** Item 10b, e.g. S. */
  transponderMode: string | null
  /** Item 18 PBN/ codes; used only when `equipment` contains R. */
  pbn?: ReadonlyArray<string> | null
  departure: string
  destination: string
  /** The points in between, in order. */
  via: string[]
  cruiseKt: number | null
  /** Total estimated elapsed time, minutes. */
  eetMinutes: number
  enduranceMinutes: number
  persons: number
  emergencyRadio: string | null
  colors: string | null
  pilotName: string
}

export const ICAO_FLIGHT_PLAN_ITEMS = [
  'aircraftId',
  'flightRules',
  'aircraftType',
  'equipment',
  'departure',
  'cruiseSpeed',
  'level',
  'route',
  'destination',
  'totalEet',
  'other',
  'endurance',
  'persons',
  'emergencyRadio',
  'colors',
  'pilot',
] as const
export type IcaoFlightPlanItem = (typeof ICAO_FLIGHT_PLAN_ITEMS)[number]

export interface IcaoFlightPlanField {
  item: IcaoFlightPlanItem
  /** The form's item number, e.g. "15". */
  number: string
  value: string
}

/** Upper case, and only what the form accepts: A-Z, 0-9, space and '/'. */
export function icaoText(value: string): string {
  return value
    .toUpperCase()
    .replace(/[ÄÅÀÁÂ]/g, 'A')
    .replace(/[ÖÒÓÔ]/g, 'O')
    .replace(/[ÜÙÚÛ]/g, 'U')
    .replace(/[ÉÈÊ]/g, 'E')
    .replace(/[^A-Z0-9 /]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** HHMM, e.g. 95 -> "0135". */
export function icaoDuration(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes))
  return `${String(Math.floor(whole / 60)).padStart(2, '0')}${String(whole % 60).padStart(2, '0')}`
}

/** N0115: knots, four digits. */
export function icaoSpeed(knots: number | null): string {
  if (knots === null || !(knots > 0)) return ''
  return `N${String(Math.min(Math.round(knots), 9999)).padStart(4, '0')}`
}

const ICAO_AERODROME = /^[A-Z]{4}$/

function aerodrome(code: string): { code: string; remark: string | null } {
  const text = icaoText(code)
  return ICAO_AERODROME.test(text) ? { code: text, remark: null } : { code: 'ZZZZ', remark: text || null }
}

export function buildIcaoFlightPlan(input: IcaoFlightPlanInput): IcaoFlightPlanField[] {
  const departure = aerodrome(input.departure)
  const destination = aerodrome(input.destination)
  const other = [
    pbnItem18(input.equipment, input.pbn) || null,
    departure.remark ? `DEP/${departure.remark}` : null,
    destination.remark ? `DEST/${destination.remark}` : null,
  ]
    .filter(Boolean)
    .join(' ')
  const equipment = [input.equipment, input.transponderMode].map((part) => icaoText(part ?? ''))
  const via = input.via.map(icaoText).filter(Boolean).join(' ')

  const fields: [IcaoFlightPlanItem, string, string][] = [
    ['aircraftId', '7', icaoText(input.registration.replace(/-/g, ''))],
    ['flightRules', '8', 'VG'],
    ['aircraftType', '9', `${icaoText(input.aircraftType ?? '') || 'ZZZZ'}/L`],
    ['equipment', '10', equipment[0] || equipment[1] ? `${equipment[0]}/${equipment[1]}` : ''],
    ['departure', '13', departure.code],
    ['cruiseSpeed', '15', icaoSpeed(input.cruiseKt)],
    ['level', '15', 'VFR'],
    ['route', '15', via || 'DCT'],
    ['destination', '16', destination.code],
    ['totalEet', '16', icaoDuration(input.eetMinutes)],
    ['other', '18', other || '0'],
    ['endurance', '19', icaoDuration(input.enduranceMinutes)],
    ['persons', '19', String(Math.max(1, Math.round(input.persons)))],
    ['emergencyRadio', '19', icaoText(input.emergencyRadio ?? '')],
    ['colors', '19', icaoText(input.colors ?? '')],
    ['pilot', '19', icaoText(input.pilotName)],
  ]
  return fields.map(([item, number, value]) => ({ item, number, value }))
}
